package org.catharsis.spendtel.core.impl

import dev.gitlive.firebase.storage.FirebaseStorage
import dev.gitlive.firebase.storage.StorageReference
import dev.gitlive.firebase.storage.storageMetadata
import io.github.aakira.napier.Napier
import org.catharsis.spendtel.core.api.StatementStorageRepo
import org.catharsis.spendtel.core.platform.PickedStatementFile
import org.catharsis.spendtel.core.utils.statementStorageLocation
import org.catharsis.spendtel.createTempFileFromByteArray
import org.catharsis.spendtel.feature.data.StoragePathInput

class StatementStorageRepoImpl(
    private val firebaseStorage: FirebaseStorage,
) : StatementStorageRepo {

    override suspend fun uploadStatements(
        userId: String,
        files: List<PickedStatementFile>,
    ): Result<List<StoragePathInput>> {
        if (files.isEmpty()) {
            return Result.failure(IllegalArgumentException("Select at least one statement to upload."))
        }

        return try {
            val uploadedPaths = files.map { file ->
                uploadSingleStatement(userId, file)
            }
            Result.success(uploadedPaths)
        } catch (e: Exception) {
            Napier.e("Statement upload failed: $e")
            Result.failure(e)
        }
    }

    private suspend fun uploadSingleStatement(
        userId: String,
        file: PickedStatementFile,
    ): StoragePathInput {
        val (storagePath, storageFileName) = statementStorageLocation(userId, file.fileName)
        val tempFile = createTempFileFromByteArray(file.bytes)

        firebaseStorage.reference(storagePath).putFile(
            tempFile,
            storageMetadata {
                contentType = file.mimeType
            },
        )

        Napier.d("Uploaded statement to $storagePath")
        return StoragePathInput(
            storagePath = storagePath,
            fileName = storageFileName,
        )
    }

    override suspend fun deleteAllUserStatements(userId: String): Result<Unit> {
        return try {
            deleteStorageTree(firebaseStorage.reference("users/$userId/statements"))
            Result.success(Unit)
        } catch (e: Exception) {
            Napier.e("Failed to delete statement uploads for $userId: $e")
            Result.failure(e)
        }
    }

    private suspend fun deleteStorageTree(reference: StorageReference) {
        val listing = reference.listAll()
        listing.items.forEach { item ->
            item.delete()
        }
        listing.prefixes.forEach { prefix ->
            deleteStorageTree(prefix)
        }
    }
}
